<template>
  <v-dialog
    v-model="dialog"
    v-on:click:outside="$emit('update:dialog', false)"
    :max-width="900"
  >
    <v-card>
      <v-card-title class="headline">
        {{ $t(`widgets.${type}-head`) }}
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pt-3">
        <v-form ref="form" class="form">
          <v-select
            v-model="item.type"
            :items="types"
            :label="$t('widgets.type')"
            :rules="[rules.required]"
            :item-text="item => $t(item.text)"
          ></v-select>
          <template v-if="item.type">
            <v-select
              v-model="item.project"
              :items="projects"
              :label="$t('widgets.project')"
              item-text="name"
              item-value="id"
              :rules="[rules.required]"
            ></v-select>
            <template v-if="item.project">
              <TreeSelector
                v-model="item.meter"
                :rules="[rules.required]"
                :label="$t('widgets.meter')"
                :items="meters"
                :options-for-tree-items="{
                  groupKey: '_groups',
                  canChooseGroup: false
                }"
              >
              </TreeSelector>
              <template v-if="item.meter">
                <v-radio-group v-model="item.options.dateType" row>
                  <v-radio
                    :label="$t('widgets.relative-dates')"
                    value="relative"
                    color="primary"
                  ></v-radio>
                  <v-radio
                    :label="$t('widgets.specific-dates')"
                    value="specific"
                    color="primary"
                  ></v-radio>
                </v-radio-group>

                <template v-if="item.options.dateType === 'specific'">
                  <v-autocomplete
                    v-model="item.options.interval"
                    :items="intervals"
                    :label="$t('widgets.interval')"
                    prepend-icon="mdi-calendar-clock"
                    :item-text="item => $t(item.text)"
                    :rules="[rules.required]"
                  ></v-autocomplete>
                  <DateInput
                    :date="item.options.date_from"
                    @update="
                      val => {
                        item.options.date_from = val;
                        this.validateIntervals();
                      }
                    "
                    :rules="[rules.requiredForDate]"
                    :label="$t('widgets.date-from')"
                  >
                  </DateInput>
                  <DateInput
                    :date="item.options.date_to"
                    @update="
                      val => {
                        item.options.date_to = val;
                        this.validateIntervals();
                      }
                    "
                    :label="$t('widgets.date-to')"
                    :rules="[rules.requiredForDate]"
                  >
                  </DateInput>
                </template>
                <template v-if="item.options.dateType === 'relative'">
                  <v-row>
                    <v-col
                      cols="6"
                      md="3"
                      v-for="select in quickSelect"
                      v-bind:key="select.title"
                      class="quick-select-col"
                    >
                      <p class="px-3 mb-0">
                        <b>{{ $t(select.title) }} </b>
                      </p>
                      <template v-for="i in select.items">
                        <v-btn
                          text
                          :color="
                            isCurrentCharts(item.options.dateTitle, i)
                              ? 'primary'
                              : undefined
                          "
                          v-on:click="setCharts(i)"
                          v-bind:key="i.title"
                          small
                          class="mt-2 px-3"
                          >{{ $t(i.title) }}</v-btn
                        >
                      </template>
                    </v-col>
                  </v-row>
                </template>
                <MeterAnalysis
                  :meter_id="item.meter"
                  :project_id="item.project"
                  :options="item.options"
                  class="mb-5"
                ></MeterAnalysis>
              </template>
            </template>
          </template>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="$emit('update:dialog', false)">
          <v-icon class="mr-2">mdi-close</v-icon>
          {{ $t("dialogs.close") }}
        </v-btn>
        <v-btn color="primary" :disabled="!canSave" @click="saveWidget">
          <v-icon class="mr-2" v-if="type === 'create'">mdi-plus</v-icon>
          <v-icon class="mr-2" v-if="type === 'edit'">mdi-pencil</v-icon>
          {{ $t(`widgets.${type}`) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import DateInput from "../common/inputs/DateInput";
import MeterAnalysis from "../analysis/MeterAnalysis";
import TreeSelector from "../common/inputs/TreeSelector";
import { rules } from "../mixins/rules";

export default {
  name: "DialogWidget",
  components: { MeterAnalysis, DateInput, TreeSelector },
  mixins: [rules],
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    widget: {}
  },
  data: () => {
    let format = "YYYY-MM-DD";
    return {
      item: {
        options: {
          dateType: "relative",
          dateTitle: "analysis.past-12-months",
          interval: "week",
          date_from: moment()
            .startOf("month")
            .subtract(12, "month")
            .format(format),
          date_to: moment()
            .endOf("month")
            .subtract(1, "month")
            .format(format)
        }
      },
      type: "create",
      intervals: []
    };
  },
  computed: {
    types() {
      return [{ value: "chart", text: "widgets.chart" }];
    },
    quickSelect() {
      return this.$store.getters["dashboardStore/getRelativeIntervals"];
    },
    projects() {
      return this.$store.getters["projectStore/getList"];
    },
    meters() {
      return this.$store.getters["projectMeteringStore/meterStore/getList"];
    },
    canSave() {
      return this.item;
    }
  },
  methods: {
    validateIntervals() {
      let disabled = true;
      if (this.date_from) {
        disabled =
          Math.abs(moment(this.date_from).diff(moment(this.date_to), "days")) >
          30;
      }
      if (disabled && (this.interval === "all" || this.interval === "hour")) {
        this.interval = "day";
      }
      this.intervals = [
        { value: "all", text: "analysis.all", disabled },
        { value: "hour", text: "analysis.hour", disabled },
        { value: "day", text: "analysis.day" },
        { value: "week", text: "analysis.week" },
        { value: "month", text: "analysis.month" },
        { value: "year", text: "analysis.year" }
      ];
    },
    saveWidget() {
      if (this.$refs.form.validate()) {
        let item = {
          ...this.item
        };
        if (item.options.dateType !== "relative") {
          delete item.options.dateTitle;
        }
        if (this.type === "create") {
          this.$store.dispatch("dashboardStore/add", { item });
        }
        if (this.type === "edit") {
          this.$store.dispatch("dashboardStore/edit", {
            widget: { ...this.widget, item: { ...this.widget.item, ...item } }
          });
        }
        this.$emit("update:dialog", false);
        this.initWidget();
      }
    },
    getProjects() {
      if (!this.item.project) {
        return;
      }
      if (!this.item.date) {
        this.$set(this.item, "date", "specific");
      }
      this.$store.dispatch("projectMeteringStore/meterStore/getList", {
        params: {
          limit: -1,
          project_id: this.item.project
        },
        onSuccess: () => {
          this.loading = false;
        }
      });
    },

    isCurrentCharts(title, charts) {
      return this.item.options.dateTitle === charts.title;
    },
    setCharts(charts) {
      this.item.options.interval = charts.item.interval;
      this.item.options.date_from = charts.item.date_from;
      this.item.options.date_to = charts.item.date_to;
      this.$set(this.item.options, "dateTitle", charts.title);
    },
    initWidget() {
      window.dispatchEvent(new Event("resize"));
      let format = "YYYY-MM-DD";
      this.item = {
        options: {
          dateType: "relative",
          dateTitle: "analysis.past-12-months",
          interval: "week",
          date_from: moment()
            .startOf("month")
            .subtract(12, "month")
            .format(format),
          date_to: moment()
            .endOf("month")
            .subtract(1, "month")
            .format(format)
        }
      };
      this.$store.dispatch("projectStore/getList", {
        params: {
          limit: -1,
          groupAccessOnly: "with"
        }
      });
      if (this.widget) {
        this.item = JSON.parse(JSON.stringify(this.widget.item));
        this.type = "edit";
      }
      this.$refs.form && this.$refs.form.resetValidation();
      this.validateIntervals();
    }
  },
  watch: {
    "item.project": {
      handler(s, d) {
        if (s !== d) {
          this.getProjects();
        }
      }
    },
    dialog() {
      this.initWidget();
    },
    widget: {
      handler() {
        this.initWidget();
      }
    },
    meters() {
      if (
        this.meters &&
        this.meters.length &&
        this.item.meter &&
        !this.meters.find(i => i.id === this.item.meter)
      ) {
        this.item.meter = "";
      }
    }
  },
  mounted() {
    this.initWidget();
  }
};
</script>

<style scoped></style>
